/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldOff, Mail, AlertTriangle, Clock, FileText } from 'lucide-react';

const Suspended = () => {
  const navigate = useNavigate();
  const [suspensionInfo, setSuspensionInfo] = useState(null);

  useEffect(() => {
    // Get suspension info from localStorage (set by API interceptor or login)
    try {
      const stored = localStorage.getItem('suspensionInfo');
      if (stored) {
        setSuspensionInfo(JSON.parse(stored));
      }
    } catch (e) {
      // Ignore parse errors
    }

    // Clear auth tokens - user should not be able to use the app
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tempToken');
    localStorage.removeItem('user');
  }, []);

  const handleBackToLogin = () => {
    localStorage.removeItem('suspensionInfo');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
        {/* Header */}
        <div className="flex flex-col items-center justify-center mb-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ShieldOff className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">
            Account Suspended
          </h1>
          <p className="text-center text-gray-600">
            Your account has been suspended due to repeated policy violations.
          </p>
        </div>

        {/* Suspension Details */}
        <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Why was my account suspended?</p>
              <p className="text-sm text-red-700 mt-1">
                Your account received 3 content policy violations, which triggered an automatic suspension
                per our Terms & Conditions (Section 4 - Content Policy).
              </p>

              {suspensionInfo?.totalViolations && (
                <p className="text-sm text-red-700 mt-2">
                  Total violations: <strong>{suspensionInfo.totalViolations}</strong>
                </p>
              )}

              {suspensionInfo?.suspendedAt && (
                <p className="text-sm text-red-700 mt-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Suspended on: {new Date(suspensionInfo.suspendedAt).toLocaleDateString('en-IN', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Violations List */}
        {suspensionInfo?.violations && suspensionInfo.violations.length > 0 && (
          <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-gray-600" />
              <p className="text-sm font-medium text-gray-700">Violation History</p>
            </div>
            <div className="space-y-2">
              {suspensionInfo.violations.map((v, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-red-500 font-medium flex-shrink-0">Strike {v.strikeNumber}:</span>
                  <span className="text-gray-700">{v.reason}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Appeal Section */}
        <div className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Appeal This Suspension</p>
              <p className="text-sm text-blue-700 mt-1">
                If you believe this suspension was made in error, you can appeal by contacting our support team:
              </p>
              <a
                href="mailto:support@skilldelivery.co.in?subject=Account%20Suspension%20Appeal"
                className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-blue-700 hover:text-blue-900 underline"
              >
                <Mail className="w-3.5 h-3.5" />
                support@skilldelivery.co.in
              </a>
              <p className="text-xs text-blue-600 mt-2">
                Please include your registered email address and a brief explanation in your appeal.
                Appeals are typically reviewed within 5-7 business days.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Login Button */}
        <button
          onClick={handleBackToLogin}
          className="w-full py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors font-medium text-sm"
        >
          Back to Login
        </button>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          SkillDelivery Platform - SKD RATWANI Technology & Software Solutions Pvt Ltd
        </p>
      </div>
    </div>
  );
};

export default Suspended;
